// UI primitives
import { Badge } from '@ui/badge';

// Shared components
import { ImageCarousel } from '../../common/ImageCarousel';

// Icons
import { EmptySkuIcon } from '../../icons';

// Hooks
import { useTranslation } from '@hooks/useTranslation';

// Utils
import { formatCurrency } from '../../../utils/formatters';

// Types
import type { ResaleItem } from '../types';

interface ResaleItemOverviewSectionProps {
  resaleItem: ResaleItem;
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-inter whitespace-nowrap text-sm font-semibold text-grey">
      {children}
    </span>
  );
}

function FieldValue({ children }: { children: React.ReactNode }) {
  return (
    <p className="mt-2 font-inter text-sm text-off-black">
      {children}
    </p>
  );
}

export function ResaleItemOverviewSection({ resaleItem }: ResaleItemOverviewSectionProps) {
  const { t } = useTranslation();

  return (
    <div>
      <h3 className="mb-4 font-kapra-expanded text-2xl font-black uppercase leading-tight text-off-black">
        {t('preview_drawer.body.title')}
      </h3>

      <div className="flex min-w-fit gap-12 rounded-input border border-dinnerplate-white bg-off-white p-6">
        <ImageCarousel
          images={(resaleItem.images ?? []).map((img) => ({ url: img.url }))}
          placeholder={<EmptySkuIcon className="h-20 w-24 text-grey" />}
          className="w-preview-image shrink-0 self-start"
        />

        <div className="flex flex-1 flex-col gap-6">
          <div>
            <FieldLabel>{t('preview_drawer.body.name')}</FieldLabel>
            <p className="mt-2 font-inter text-sm font-semibold text-off-black">
              {resaleItem.name}
            </p>
          </div>

          <div className="flex gap-12">
            <div className="min-w-0 flex-1">
              <FieldLabel>{t('preview_drawer.body.vendor')}</FieldLabel>
              <div className="mt-2">
                {resaleItem.vendor ? (
                  <Badge
                    variant="static"
                    className="font-inter flex w-fit items-center justify-center rounded-xs border-0 bg-dinnerplate-white px-1.5 py-1 text-xs font-semibold leading-tight-sm text-grey"
                  >
                    {resaleItem.vendor}
                  </Badge>
                ) : (
                  <span className="font-inter text-sm text-grey">—</span>
                )}
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <FieldLabel>{t('preview_drawer.body.daily_limit')}</FieldLabel>
              <FieldValue>{resaleItem.max_quantity ?? '—'}</FieldValue>
            </div>
          </div>

          <div className="flex gap-12">
            <div className="min-w-0 flex-1">
              <FieldLabel>{t('preview_drawer.body.retail_price')}</FieldLabel>
              <FieldValue>{formatCurrency(resaleItem.recommended_retail_price)}</FieldValue>
            </div>
            <div className="min-w-0 flex-1">
              <FieldLabel>{t('preview_drawer.body.cost_price')}</FieldLabel>
              <FieldValue>{formatCurrency(resaleItem.cost_to_company)}</FieldValue>
            </div>
          </div>

          <div className="flex gap-12">
            <div className="min-w-0 flex-1">
              <FieldLabel>{t('preview_drawer.body.item_notes')}</FieldLabel>
              <FieldValue>{resaleItem.item_notes || '—'}</FieldValue>
            </div>
            <div className="min-w-0 flex-1">
              <FieldLabel>{t('preview_drawer.body.delivery_details')}</FieldLabel>
              <p className="mt-2 whitespace-pre-wrap font-inter text-sm text-off-black">
                {resaleItem.delivery_information || '—'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
